<template>
    <div>
        <svg
            :width="width"
            :height="height"
            overflow="visible"
        >
            <text
                v-if="values[0]"
                class="group-label left"
                :y="0"
                dy="0.5em"
                :x="width / 2 - middleGap"
            >{{ values[0].group }}</text>
            <text
                v-if="values[1]"
                class="group-label right"
                :y="0"
                dy="0.5em"
                :x="width / 2 + middleGap"
            >{{ values[1].group }}</text>
            <rect
                v-for="(rect, i ) in rects"
                :key="i"
                :class="{
                    bar: true,
                    highlight: rect.highlight
                }"
                v-bind="rect"
                @mouseover="onMouseEnter(rect)"
                @mouseout="onMouseLeave"
            />
            <path
                v-for="(line, i) in lines"
                :key="`line-${i}`"
                class="compare-line"
                :d="line"
            />

            <text
                v-for="{x, label} in xAxis"
                :key="`axis-x-${x}`"
                class="axis-tick x"
                :x="x"
                :y="height"
                dy="-0.1em"
            >{{ label }}</text>

            <text
                v-for="{y, label} in yAxis"
                :key="`axis-y-${y}`"
                class="axis-tick y"
                :x="width / 2"
                :y="y"
            >
                {{ label }}
            </text>
        </svg>

        <div
            v-if="hoveredRect"
            ref="tooltip"
            class="tooltip-content"
            :style="tooltipPos"
        >
            <slot
                name="tooltip-content"
                :value="hoveredRect.value"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';

const { scaleLinear, scaleBand } = d3;
const { line } = d3;

const sigDigs = n => num => +num.toExponential(n - 1);

export default {
    props: {
        values: {
            type: Array,
            default: () => []
        },
        compareValues: {
            type: Array,
            default: () => []
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 100
        },
        middleGap: {
            type: Number,
            default: 15
        }
    },
    data() {
        return {
            hoveredRect: null,
            padBottom: 12,
            padTop: 14
        };
    },
    computed: {
        valuesNormalized() {
            return _(this.values)
                .map(({ values, ...group }) => ({
                    ...group,
                    values: _.map(values, ({ x, y, ...rest }) => ({
                        y,
                        x: x / _(this.values).flatMap('values').sumBy('x'),
                        rawX: x,
                        ...rest
                    }))
                }))
                .value();
        },
        compareValuesNormalized() {
            return _(this.compareValues)
                .map(({ values, ...group }) => ({
                    ...group,
                    values: _.map(values, ({ x, y, ...rest }) => ({
                        y,
                        x: x / _(this.compareValues).flatMap('values').sumBy('x'),
                        rawX: x,
                        ...rest
                    }))
                }))
                .value();
        },
        xScale() {
            return scaleLinear()
                .domain([0, _([...this.valuesNormalized, ...this.compareValuesNormalized])
                    .flatMap('values')
                    .map(v => sigDigs(1)(v.x))
                    .maxBy()])
                .range([0, this.width / 2 - this.middleGap]);
        },
        yScale() {
            return scaleBand()
                .padding(0.1)
                .domain(_(this.valuesNormalized)
                    .flatMap('values')
                    .map('y')
                    .uniqBy()
                    .value())
                .range([this.height - this.padBottom, this.padTop]);
        },
        rectXScale() {
            return scaleLinear()
                .domain([0, _(this.values)
                    .flatMap('values')
                    .map(v => sigDigs(1)(v.x))
                    .maxBy()])
                .range([0, this.width / 2 - this.middleGap]);
        },
        rects() {
            if (this.valuesNormalized.length === 0) {
                return [];
            }

            const yScale = this.yScale;
            const xScale = this.xScale;

            return [
                ..._.map(this.valuesNormalized[0].values, ({ x, y, ...rest }) => ({
                    x: this.width / 2 - this.middleGap - xScale(x),
                    y: yScale(y),
                    width: xScale(x),
                    maxX: this.width / 2 - this.middleGap,
                    height: yScale.bandwidth(),
                    value: {
                        x,
                        y,
                        ...rest,
                        group: this.valuesNormalized[0].group,
                        ..._(_(this.compareValuesNormalized[0].values)
                            .find(({ y })))
                            .pick(['x', 'rawX'])
                            .mapKeys((v, key) => `compare${_.upperFirst(key)}`)
                            .value()
                    }
                })),
                ..._.map(this.valuesNormalized[1].values, ({ x, y, ...rest }) => ({
                    x: this.width / 2 + this.middleGap,
                    y: yScale(y),
                    width: xScale(x),
                    maxX: this.width / 2 + this.middleGap + xScale.range()[1],
                    height: yScale.bandwidth(),
                    value: {
                        x,
                        y,
                        ...rest,
                        group: this.valuesNormalized[1].group,
                        ..._(_(this.compareValuesNormalized[1].values)
                            .find(({ y })))
                            .pick(['x', 'rawX'])
                            .mapKeys((v, key) => `compare${_.upperFirst(key)}`)
                            .value()
                    }
                }))
            ];
        },
        xAxis() {
            // eslint-disable-next-line no-restricted-globals
            if (isNaN(this.xScale.domain()[0]) || isNaN(this.xScale.domain()[1])) {
                return [];
            }
            // const [mid] = this.rectXScale.ticks(2);
            const [, max] = this.xScale.domain();

            const valueToObj = v => ({ label: `${Math.floor(Math.abs(v) * 100)}%`, x: this.xScale(v) + this.width / 2 });

            const twoSigDigs = sigDigs(2);
            return [
                ...[twoSigDigs(max), -twoSigDigs(max), twoSigDigs(max / 2), -twoSigDigs(max / 2)].map(valueToObj)
            ];
        },
        yAxis() {
            // eslint-disable-next-line no-restricted-globals
            if (isNaN(this.yScale.domain()[0]) || isNaN(this.yScale.domain()[1])) {
                return [];
            }
            const values = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];
            return values.map((v, i) => ({
                label: i === values.length - 1 ? `${v}+` : v,
                y: this.yScale(v) + this.yScale.bandwidth() * 2 / 3 // Should be 1/2, not sure why 2/3 ends up being a better position
            }));
        },
        lines() {
            if (this.compareValues.length === 0) {
                return [];
            }

            const yScale = this.yScale;

            const xScale = this.xScale;

            const leftLine = line()
                .x(({ x }) => this.width / 2 - this.middleGap - xScale(x))
                .y(({ y }) => yScale(y) + yScale.bandwidth() / 2);
            const rightLine = line()
                .x(({ x }) => this.width / 2 + this.middleGap + xScale(x))
                .y(({ y }) => yScale(y) + yScale.bandwidth() / 2);

            return [
                leftLine(this.compareValuesNormalized[0].values),
                rightLine(this.compareValuesNormalized[1].values)
            ];
        },
        tooltipPos() {
            if (this.$screens.mobile && this.hoveredRect.value.group === 'Female') {
                return {
                    right: `${this.hoveredRect.x - 25}px`,
                    top: `${this.hoveredRect.y}px`
                };
            }
            return {
                left: `${this.hoveredRect.maxX + 5}px`,
                top: `${this.hoveredRect.y}px`
            };
        }
    },
    async updated() {
        await this.$nextTick();
        if (this.hoveredRect) {
            this.fixTooltipPosition();
        }
    },
    methods: {
        onMouseEnter(value) {
            this.hoveredRect = value;
        },
        onMouseLeave() {
            this.hoveredRect = null;
        },
        fixTooltipPosition() {
            const rect = this.$refs.tooltip.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                // Adjust tooltip to be on screen with 5px padding
                const adjustment = (rect.right - window.innerWidth) + 25;
                this.$refs.tooltip.style.transform = `translateX(-${adjustment}px)`;
            } else if (rect.left < 0) {
                const adjustment = -rect.left + 5;
                this.$refs.tooltip.style.transform = `translateX(${adjustment}px)`;
            }
        }
    }
};
</script>

<style src="./common.chart.css" scoped></style>
<style lang="postcss" scoped>
svg {
    overflow: visible !important;
}
rect {
    @apply fill-current;
}
rect.bar {
    @apply cursor-pointer;
}
rect.bar:hover {
    @apply opacity-75;
}
line {
    @apply stroke-current text-white;
    stroke-width: 0.5px;
}

path {
    @apply stroke-current text-neutral-2 opacity-75 pointer-events-none;
    fill: none;
    fill-opacity: 0;
    stroke-width: 3px;
}

text.axis-tick {
    @apply text-neutral-label fill-current text-2xs;
}

text.axis-tick.x {
    text-anchor: middle;
}
text.axis-tick.y {
    alignment-baseline: middle;
    text-anchor: middle;
}

text.group-label {
    @apply text-neutral-label fill-current text-xs;
}

text.group-label.left {
    text-anchor: end;
}
text.group-label.right {
    text-anchor: start;
}
</style>
